/**
 * What the calling principal may do, asked of the service.
 *
 * The desk renders its affordances from this. It is advisory in exactly one
 * direction: every route refuses on its own reading of the same principal, so a
 * client that got this wrong — or lied about it — gains a button that answers
 * `403`. Hiding an affordance is a courtesy, never a control.
 *
 * Keyed on the credential's epoch rather than on the credential. A query key is
 * held in the cache, shown in devtools and walked by anything that serialises
 * state, so the key itself must never be one; and keying on the path alone
 * would serve a viewer's cached answer to somebody who has since signed in.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { credentialHeaders, useCredential } from '../lib/credential';
import type { Granted } from '../lib/types';

const ANONYMOUS: Granted = { role: 'viewer', capabilities: ['read_registry'] };

export type Capabilities = {
  role: string;
  /** Whether the caller holds one capability. The only question a view asks. */
  may: (capability: string) => boolean;
  isLoading: boolean;
};

export function useCapabilities(): Capabilities {
  const { key, epoch } = useCredential();
  const query = useQuery({
    queryKey: ['capabilities', epoch],
    queryFn: () => api<Granted>('/api/capabilities', { headers: credentialHeaders(key) }),
    // A credential the service does not recognise still reads, so a refusal here
    // is the viewer tier rather than a failure: the desk should render the
    // public view, not an error panel.
    retry: false,
  });
  const granted = query.data ?? ANONYMOUS;
  return {
    role: granted.role,
    may: (capability: string) => granted.capabilities.includes(capability),
    isLoading: query.isLoading,
  };
}
